import { FC, useState } from "react";
import { useTranslation } from "react-i18next";

import styles from "./RecalculationConfirmModal.module.scss";
import { Button } from "src/components";
import {
  Cross,
  Loader,
  Triangle,
  RadioButtonCheck,
  RadioButtonEmpty,
} from "src/assets/icons";
import { useModal, useKeyboardListener } from "src/hooks";
import { SelectedSearch } from "src/features/SearchKeywords/components";
import { ConfirmRecalculateTopicsModal } from "src/features/Modals/ConfirmRecalculateTopicsModal/ConfirmRecalculateTopicsModal";

export type RecalculationMode = "full" | "future";

type Props = {
  search?: Search.CreationData;
  view?: "keywords" | "searches";
  onRecalculate: (mode: "full" | "future") => Promise<void>;
  onCancel: () => void;
};

export const RecalculationConfirmModal: FC<Props> = ({
  search,
  view = "keywords",
  onRecalculate,
  onCancel,
}) => {
  const { t } = useTranslation();
  const { setModal, closeModal } = useModal();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedMode, setSelectedMode] = useState<RecalculationMode>("future");
  const [isAdvancedExpanded, setIsAdvancedExpanded] = useState(false);

  const handleClose = () => {
    if (isLoading) return;
    onCancel();
    closeModal("recalculation-confirm");
  };

  useKeyboardListener({
    key: "Escape",
    type: "keydown",
    callback: handleClose,
  });

  const handleToggleAdvanced = () => {
    if (isAdvancedExpanded) {
      setIsAdvancedExpanded(false);
      setSelectedMode("future");
    } else {
      setIsAdvancedExpanded(true);
    }
  };

  const handleRecalculateAllConfirmed = async () => {
    setIsLoading(true);
    try {
      await onRecalculate("full");
      closeModal("recalculation-confirm");
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (selectedMode === "full") {
      setModal(
        "confirm-recalculate-topics",
        <ConfirmRecalculateTopicsModal
          onConfirm={handleRecalculateAllConfirmed}
          onCancel={() => closeModal("confirm-recalculate-topics")}
        />,
      );
      return;
    }

    setIsLoading(true);
    try {
      await onRecalculate("future");
      closeModal("recalculation-confirm");
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const descriptionKey =
    view === "searches"
      ? "component.modal.recalculation_confirm.description_searches"
      : "component.modal.recalculation_confirm.description";

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div className={styles.title}>
          <span>{t("component.modal.recalculation_confirm.title")}</span>
        </div>
        <Button
          onClick={handleClose}
          disabled={isLoading}
          className={styles.closeButton}
          buttonSize="small"
          buttonStyle="outlined"
        >
          <Cross />
        </Button>
      </div>

      <div className={styles.content}>
        {search && (
          <div className={styles.searchInfo}>
            <SelectedSearch search={search} />
          </div>
        )}

        <p className={styles.description}>
          <strong>{t(descriptionKey)}</strong>
        </p>

        <div className={styles.optionsSection}>
          <div
            className={`${styles.radioOption} ${
              selectedMode === "future" ? styles.selected : ""
            }`}
            onClick={() => setSelectedMode("future")}
          >
            <div className={styles.radioIcon}>
              {selectedMode === "future" ? (
                <RadioButtonCheck />
              ) : (
                <RadioButtonEmpty />
              )}
            </div>
            <div className={styles.radioContent}>
              <div className={styles.radioLabel}>
                {t(
                  "component.modal.recalculation_confirm.option_future_only.title",
                )}
              </div>
              <div className={styles.radioDescription}>
                {t(
                  "component.modal.recalculation_confirm.option_future_only.description",
                )}
              </div>
            </div>
          </div>

          <button
            className={`${styles.advancedToggle} ${
              isAdvancedExpanded ? styles.expanded : ""
            }`}
            onClick={handleToggleAdvanced}
            type="button"
          >
            <Triangle className={styles.triangleIcon} />
            <span>{t("component.modal.recalculation_confirm.advanced")}</span>
          </button>

          {isAdvancedExpanded && (
            <div
              className={`${styles.radioOption} ${styles.advancedOption} ${
                selectedMode === "full" && styles.selected
              }`}
              onClick={() => setSelectedMode("full")}
            >
              <div className={styles.radioIcon}>
                {selectedMode === "full" ? (
                  <RadioButtonCheck />
                ) : (
                  <RadioButtonEmpty />
                )}
              </div>
              <div className={styles.radioContent}>
                <div className={styles.radioLabel}>
                  {t("component.modal.recalculation_confirm.option_full.title")}
                </div>
                <div className={styles.radioDescription}>
                  {t(
                    "component.modal.recalculation_confirm.option_full.description",
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className={styles.buttonsWrapper}>
        <Button
          onClick={handleClose}
          disabled={isLoading}
          buttonStyle="outlined"
        >
          {t("component.modal.recalculation_confirm.button.cancel")}
        </Button>

        <Button onClick={handleSave} disabled={isLoading}>
          {isLoading ? (
            <Loader className={styles.loader} />
          ) : (
            t("component.modal.recalculation_confirm.button.save")
          )}
        </Button>
      </div>
    </div>
  );
};
